import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import * as XLSX from 'xlsx';
import * as XlsxPopulate from 'xlsx-populate';
import { NotificationHandlerService } from '../notifications/notification-handler.service';
import { ImportColumnCheckboxModel } from '../models/ImportColumnCheckboxModel';

@Injectable()
export class SheetService {

    // Create new workbook related properties
    private workbook: any;
    private sheetsCount: number;

    // Importing workbook related properties
    private importWorkbooks: any[];
    private importWorkbooksInfo: any[];
    private hasHeader: boolean;
    private headerRow: any[];

    // Clients subscribe so that they know if a worksheet is being loaded.
    public importInProgress = new BehaviorSubject<boolean>(true);

    // Used to notify export component to prompt used for column selection.
    public promptColumnSelection = new BehaviorSubject<Array<any>>(null);

    // Used to notify subscribers that a sheet has been added to workbook
    public sheetColumnSelection = new BehaviorSubject<Array<any>>(null);

    // Used to notify subscribers that a workbook has been downloaded
    public workbookDownloaded = new BehaviorSubject<boolean>(false);

    constructor(private _notificationService: NotificationHandlerService) {
        XlsxPopulate.fromBlankAsync()
        .then((workbook: any) => {
            this.workbook = workbook;
        });
        this.sheetsCount = 0;
        this.importWorkbooks = [];
        this.importWorkbooksInfo = [];
        this.headerRow = [];
    }

    // Broadcast to prompt user for column selection
    public promptColumnSelect(data: any, sheetName = '', multiReport = false, callerName: string): void {
        this.promptColumnSelection.next([data, sheetName, multiReport, callerName]);
    }

    // Broadcast to subscribers user column selection
    public replySelectionToCaller(rememberSelection: boolean, selectedColumns: ImportColumnCheckboxModel[], waitingForSelection: boolean, callerName: string): void {
        this.sheetColumnSelection.next([rememberSelection, selectedColumns, waitingForSelection, callerName]);
    }

    // Broadcast to subscribers while importing data from a workbook
    public updateImportStatus(status: boolean): void {
        // Broadcast change in import loading status
        this.importInProgress.next(status);
    }

    // Get the count of sheets in the current workbook
    public getSheetCount(): number {
        return this.sheetsCount
    }

    // Add a new worksheet to the current workbook from the passed parameters
    public addSheet(data: any, selectedColumns: ImportColumnCheckboxModel[], sheetName: string) {
        this._notificationService.logConsoleInfo(sheetName);
        let workSheet = this.workbook.addSheet(sheetName);
        this.sheetsCount += 1;
        let deferred = new Promise((resolve, reject) => { resolve(true) })
        // Get promise from populate worksheet
        let addWorksheet = this.populateWorksheet(data, selectedColumns, workSheet, sheetName);
        // Chain returned promise from this function to promise returned from populateWorksheet function
        return new Promise((resolve, reject) => {
            addWorksheet.then(() => {
                resolve(true);
            });
        });
    }

    // Clear current export workbook and reset sheet count
    public clearExport(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.sheetsCount = 0;
            let result = XlsxPopulate.fromBlankAsync()
                .then((workbook: any) => {
                    this.workbook = workbook;
                });
            resolve(result);
        });
    }

    // Clear current import data
    public clearImport(): void {
        this.importWorkbooks = [];
        this.importWorkbooksInfo = [];
        this.hasHeader = true;
        this.headerRow = [];
    }

    // Get workbooks data
    public getImportWorkbooks(): any {
        return this.importWorkbooks;
    }

    // Get header data -- column titles
    public getHeaderRow(): any[] {
        return this.headerRow;
    }

    // Load data from imported workbook into array
    public loadWorkbook(file: any, hasHeader: boolean): void {
        this.updateImportStatus(true);
        this.hasHeader = hasHeader;
        XlsxPopulate.fromDataAsync(file)
            .then((workbook: any) => {
                let range = workbook.sheet(0).usedRange().value();
                this.headerRow = range[0];
                // Remove the header row if it exists
                if (hasHeader) {
                    range.splice(0, 1);
                }
                this.importWorkbooks.push(range);
                let colCount = range[0].length;
                let rowCount = range.length;
                let workbookInfo = [];
                workbookInfo.push(colCount);
                workbookInfo.push(rowCount);
                this.importWorkbooksInfo[0] = workbookInfo;
                this.updateImportStatus(false);
            });

    }

    // Create workbook download and clear workbook data afterwards
    public downloadWorkbook(name: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            let result = this.createDownload(name);
            this.clearExport().then((result) => {
                resolve(result);
            });
        });
    }

    // Given a row index and column index return a cell name eg: A1
    private cellName(row: number, col: number): string {
        let ordA = 'a'.charCodeAt(0);
        let ordZ = 'z'.charCodeAt(0);
        let len = ordZ - ordA + 1;

        let s = '';
        while (col >= 0) {
            s = String.fromCharCode(col % len + ordA) + s;
            col = Math.floor(col / len) - 1;
        }
        return (s + (row + 1)).toUpperCase();
    }

    // Given a column index reurn the coumn name eg: AA
    private colName(col: number): string {
        let ordA = 'a'.charCodeAt(0);
        let ordZ = 'z'.charCodeAt(0);
        let len = ordZ - ordA + 1;

        let s = '';
        while (col >= 0) {
            s = String.fromCharCode(col % len + ordA) + s;
            col = Math.floor(col / len) - 1;
        }
        return s.toUpperCase();
    }

    // Remove the blank default sheet and create the workbook download
    private createDownload(name: string): void {
        if (this.sheetsCount > 0) {
            this.workbook.deleteSheet('Sheet1');
        }
        this.workbook.outputAsync()
            .then((blob: any) => {
                if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                    // If IE, you must uses a different method.
                    window.navigator.msSaveOrOpenBlob(blob, name + '.xlsx');
                } else {
                    let url = window.URL.createObjectURL(blob);
                    let a = document.createElement('a');
                    document.body.appendChild(a);
                    a.href = url;
                    a.download = name + '.xlsx';
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                }
            });
    }

    // Populate and style worksheet for exporting
    private populateWorksheet(data: any, selectedColumns: ImportColumnCheckboxModel[], workSheet: any, name: string): Promise<boolean> {
        let isError = false;
        if (data instanceof Array && data.length > 0) {
            try {
                let rowNum = 0;
                let headerVals = [];

                // Loop through the first object getting names of the proprties for header row
                for (let key in data[0]) {
                    let selected = selectedColumns.find(o => o.columnKey === key && o.includeColumn === true);
                    if (data[0].hasOwnProperty(key) && selected) {
                        headerVals.push(key);
                    }
                }

                // Create header of worksheet
                for (let i = 0; i < headerVals.length; i++) {
                    let val = headerVals[i];
                    let cellName = this.cellName(rowNum, i);
                    let cell = workSheet.cell(cellName);
                    cell.value(val);
                    cell.style('bold', true);
                    cell.style('fill', 'bbdefb');
                }
                // Increment rownum so that the header is not overwritten
                rowNum++;

                // Populate sheet with data
                for (let d = 0; d < data.length; d++) {
                    let columnNum = 0;
                    let currentData = data[d];
                    for (let key in currentData) {
                        let selected = selectedColumns.find(o => o.columnKey === key && o.includeColumn === true);
                        if (currentData.hasOwnProperty(key) && selected) {
                            let val = currentData[key];
                            let cellName = this.cellName(rowNum, columnNum);
                            let cell = workSheet.cell(cellName);
                            cell.value(val);
                            columnNum++;
                        }
                    }
                    rowNum++;
                }

                // Set all cells to have a border
                let range = workSheet.usedRange();
                range.style('border', true);

                // Set all columns width to 35
                for (let col = 0; col < headerVals.length; col++) {
                    let colName = this.colName(col);
                    workSheet.column(colName).width(25);
                }
                //this._notificationService.logConsoleInfo('Worksheet ' + name + ' added successfully!');

            } catch (e) {
                this._notificationService.logError(e);
                isError = true;
            }
            //} finally {
            //// After loop has finished return promise
            //    return new Promise((resolve, reject) => {
            //        resolve(isError)
            //    });
            //}
        }
        // If passed data is not array return promise
        this._notificationService.logConsoleInfo('Worksheet ' + name + ' added successfully!');
            return new Promise((resolve, reject) => {
                return resolve(isError)
            });
    }
}


// WEBPACK FOOTER //
// ./angularApp/app/shared/sheet-export-import/sheet.service.ts